import React from 'react';
import type { GuestSeat } from '../../../models/booking/GuestSeat';
import './SeatRows.css';

interface Props {
  /** Guest seats only — seat 1 is always the signed-in member and renders read-only. */
  seats: GuestSeat[];
  memberName: string;
  onChange: (seats: GuestSeat[]) => void;
  /**
   * Per-seat rates for the CHOSEN SLOT, in cents, straight from the availability API — the same
   * numbers the funnel quotes. They are display only: the amount actually charged is the server's
   * figure on the 202, never anything computed here.
   * Undefined until a slot is picked, in which case no price is shown at all rather than a zero.
   */
  proSeatCents?: number;
  esportsSeatCents?: number;
  /* The member's own simulator. It lives on their seat row rather than a separate "Your seat"
     field, so one booking has ONE place where each person's simulator is chosen. */
  memberRigClass?: GuestSeat['rigClass'];
  onMemberRigChange?: (rig: GuestSeat['rigClass']) => void;
  /**
   * Whether a seat may switch to a simulator, given what the chosen slot has free.
   *
   * Automatic fitting only covers seats nobody has touched; without this the toggles stayed
   * clickable and a member could still put two seats on a single Pro rig.
   * Seat 0 is the member, guests follow in order. Absent = no slot chosen, so nothing is limited.
   */
  canUse?: (seatIndex: number, rig: GuestSeat['rigClass']) => boolean;
  /** What the member's own seat costs, at the end of their row. A pass seat is "Included"; empty shows nothing. */
  memberPriceLabel?: string;
  /** Colour by meaning: green for included, amber for credits, plain for a card price. */
  memberPriceTone?: 'included' | 'credits' | 'card';
  /**
   * The simulator classes on offer. A class with no rigs at all (a race night with no Esports rigs) is
   * not drawn as a choice. Absent = both.
   */
  rigs?: GuestSeat['rigClass'][];
}

// 'Pro', matching rigLabel in EntitlementBooking — the member's own seat buttons say Pro, so a
// guest toggle saying "Full Motion" made one booking use two names for one thing.
const RIGS: { key: GuestSeat['rigClass']; label: string }[] = [
  { key: 'pro', label: 'Pro' },
  { key: 'esports', label: 'Esports' },
];

const money = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

const SeatRows: React.FC<Props> = ({
  seats, memberName, onChange, proSeatCents, esportsSeatCents,
  memberRigClass, onMemberRigChange, canUse,
  memberPriceLabel = 'Included', memberPriceTone = 'included', rigs,
}) => {
  const rigChoices = rigs ? RIGS.filter((r) => rigs.includes(r.key)) : RIGS;
  const priceClass = { included: 'seat-included', credits: 'seat-credits', card: 'seat-card' }[memberPriceTone];
  const update = (index: number, key: keyof GuestSeat, value: string) => {
    onChange(seats.map((s, i) => (i === index ? { ...s, [key]: value } : s)));
  };

  const remove = (index: number) => onChange(seats.filter((_, i) => i !== index));

  const seatCents = (rig: GuestSeat['rigClass']): number | undefined =>
    (rig === 'pro' ? proSeatCents : esportsSeatCents);

  return (
    <div className="seat-list">
      <div className="seat-member">
        <span className="seat-member-name">{memberName}</span>
        <span className="seat-you">you</span>

        {memberRigClass && onMemberRigChange && (
          <div className="seat-rig" role="group" aria-label="Your simulator">
            {rigChoices.map((rig) => (
              <button
                key={rig.key}
                type="button"
                className="seat-rig-btn"
                aria-pressed={memberRigClass === rig.key}
                disabled={canUse ? !canUse(0, rig.key) : false}
                title={canUse && !canUse(0, rig.key)
                  ? `No ${rig.label} simulator free at this time — move another driver off it first`
                  : undefined}
                onClick={() => onMemberRigChange(rig.key)}
              >
                {rig.label}
              </button>
            ))}
          </div>
        )}

        {memberPriceLabel && <span className={priceClass}>{memberPriceLabel}</span>}
      </div>

      {seats.map((seat, index) => {
        const cents = seatCents(seat.rigClass);
        return (
          <div className="seat-guest" key={index}>
            <div className="seat-guest-head">
              <span className="seat-guest-title">Guest {index + 1}</span>

              <div className="seat-rig" role="group" aria-label={`Simulator for guest ${index + 1}`}>
                {rigChoices.map((rig) => (
                  <button
                    key={rig.key}
                    type="button"
                    className="seat-rig-btn"
                    // aria-pressed carries the selection for assistive tech AND for tests: a
                    // background colour alone says nothing about which seat is chosen.
                    aria-pressed={seat.rigClass === rig.key}
                    disabled={canUse ? !canUse(index + 1, rig.key) : false}
                    title={canUse && !canUse(index + 1, rig.key)
                      ? `No ${rig.label} simulator free at this time — move another driver off it first`
                      : undefined}
                    onClick={() => update(index, 'rigClass', rig.key)}
                  >
                    {rig.label}
                  </button>
                ))}
              </div>

              {cents !== undefined && <span className="seat-guest-price">{money(cents)}</span>}

              <button
                type="button"
                className="seat-remove"
                aria-label={`Remove guest ${index + 1}`}
                onClick={() => remove(index)}
              >
                Remove
              </button>
            </div>

            <div className="seat-fields">
              <div className="seat-field">
                <label htmlFor={`guest-${index}-first`}>
                  First name <span className="seat-required" aria-hidden="true">*</span>
                </label>
                <input
                  id={`guest-${index}-first`}
                  className="form-control"
                  placeholder="First name (required)"
                  value={seat.firstName}
                  required
                  onChange={(e) => update(index, 'firstName', e.target.value)}
                />
              </div>
              <div className="seat-field">
                <label htmlFor={`guest-${index}-last`}>
                  Last name <span className="seat-required" aria-hidden="true">*</span>
                </label>
                <input
                  id={`guest-${index}-last`}
                  className="form-control"
                  placeholder="Last name (required)"
                  value={seat.lastName ?? ''}
                  required
                  onChange={(e) => update(index, 'lastName', e.target.value)}
                />
              </div>
              <div className="seat-field">
                <label htmlFor={`guest-${index}-email`}>Email</label>
                <input
                  id={`guest-${index}-email`}
                  type="email"
                  className="form-control"
                  placeholder="Email (optional)"
                  value={seat.email ?? ''}
                  onChange={(e) => update(index, 'email', e.target.value)}
                />
              </div>
            </div>
          </div>
        );
      })}

      {seats.length > 0 && (
        <div className="seat-why">
          <p className="seat-why-title">Why we ask for a guest&rsquo;s email</p>
          <ul className="seat-why-list">
            <li>
              <span className="seat-why-icon" aria-hidden="true">&rarr;</span>
              <span><b>Their lap times follow them.</b> Times recorded today attach to their
                account instead of sitting on a record they can never claim.</span>
            </li>
            <li>
              <span className="seat-why-icon" aria-hidden="true">&rarr;</span>
              <span><b>Licence progress carries over.</b> A returning guest keeps the level they
                have earned rather than starting again.</span>
            </li>
            <li>
              <span className="seat-why-icon" aria-hidden="true">&rarr;</span>
              <span><b>We match them instead of duplicating them.</b> An email finds the driver
                already on file, so they do not become a second record to merge later.</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default SeatRows;
